import { endOfMonth, format, isValid, parse } from 'date-fns';
import {
  AdviseCurrentSupplier,
  AdviseCurrentSuppliers,
  AdviseSupplierContract,
  AdviseSupplierInvoice,
  AdviseSupplierLineItem,
  SupplierLineItemsReplacement
} from '../../../../types/advise';

export const INVOICE_LINES_PER_PAGE = 8;

type LineChange = Partial<Pick<AdviseSupplierLineItem, 'in_scope' | 'category'>>;
type SupplierChange = Partial<Pick<AdviseCurrentSupplier, 'include_taxes' | 'contract'>>;

export const mostRecentInvoice = (supplier: AdviseCurrentSupplier) =>
  supplier.invoices.find((invoice) => invoice.id === supplier.most_recent_invoice_id) ?? null;

export const billingPeriodStart = (period?: string | null) => {
  if (!period) return null;
  const start = parse(period, 'yyyy-MM', new Date());
  return isValid(start) ? start : null;
};

export const billingPeriodLabel = (period?: string | null) => {
  const start = billingPeriodStart(period);
  return start && `${format(start, 'MMM d')} – ${format(endOfMonth(start), 'd, yyyy')}`;
};

const withSupplier = (
  current: AdviseCurrentSuppliers,
  supplierId: string,
  update: (supplier: AdviseCurrentSupplier) => AdviseCurrentSupplier
): AdviseCurrentSuppliers => ({
  ...current,
  suppliers: current.suppliers?.map((supplier) => (supplier.id === supplierId ? update(supplier) : supplier))
});

export const withSupplierChange = (current: AdviseCurrentSuppliers, supplierId: string, change: SupplierChange) =>
  withSupplier(current, supplierId, (supplier) => ({ ...supplier, ...change }));

export const withContractChange = (
  current: AdviseCurrentSuppliers,
  supplierId: string,
  change: AdviseSupplierContract
) => withSupplier(current, supplierId, (supplier) => ({ ...supplier, contract: { ...supplier.contract, ...change } }));

const withLineChange = (invoice: AdviseSupplierInvoice, lineId: string, change: LineChange) => ({
  ...invoice,
  line_items: invoice.line_items.map((line) => (line.id === lineId ? { ...line, ...change } : line))
});

export const withInvoiceLineChange = (
  current: AdviseCurrentSuppliers,
  supplierId: string,
  invoiceId: string,
  lineId: string,
  change: LineChange
) =>
  withSupplier(current, supplierId, (supplier) => ({
    ...supplier,
    invoices: supplier.invoices.map((invoice) =>
      invoice.id === invoiceId ? withLineChange(invoice, lineId, change) : invoice
    )
  }));

export const lineItemsReplacement = (invoice: AdviseSupplierInvoice): SupplierLineItemsReplacement => ({
  version: invoice.line_items_version,
  line_items: invoice.line_items.map(({ id, in_scope, category }) => ({ id, in_scope, category }))
});

export const pageCount = (total: number) => Math.max(1, Math.ceil(total / INVOICE_LINES_PER_PAGE));

export const linesOnPage = <T>(lines: T[], page: number) =>
  lines.slice(page * INVOICE_LINES_PER_PAGE, (page + 1) * INVOICE_LINES_PER_PAGE);

export const showingCaption = (shown: number, total: number) =>
  `Showing ${shown} of ${total} line items · uncheck lines for services outside this evaluation`;

export const categoryOptions = (categories: string[], current: string) =>
  categories.includes(current) ? categories : [...categories, current];
